/**
 * BuilderEditorProvider — the single source of truth for the manual editing shell.
 *
 *   Builder Page → Builder Editor Context → Runtime Theme → Builder Engine → Components
 *
 * It holds ONE working SiteSchema. Design/Settings edits update it immediately so the
 * Builder Engine re-renders live from the same schema — but NOTHING is persisted until
 * the user clicks Save. save() PATCHes the allowed fields and refreshes the query cache;
 * closing/navigating without saving simply discards the working copy (a refresh reloads
 * the last saved version from the backend, the single source of truth).
 */
import { useCallback, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { siteBuilderApi } from '../../api/client'
import type { BuilderRecord, BuilderUpdate } from '../../api/client'
import type {
  AssetType,
  BusinessInfo,
  SectionContent,
  SiteSchema,
  Section,
  SectionKind,
  ContentItem,
  MediaItem,
  Action,
  LayoutId,
} from '../../site-builder/schema'
import { DESIGN_SYSTEMS } from '../../site-builder/registry'
import {
  fontStack,
  SPACING_Y,
  RADIUS,
  RADIUS_LG,
  REFERENCE_THEME,
  REFERENCE_BRAND_COLOR,
  themeOverrideVars,
} from '../../site-builder/components/shared/theme/themeVars'
import { BuilderEditorCtx, applyBusinessInfoToContent, mapEmbedUrl } from './editorContext'
import type {
  BuilderEditorValue,
  SaveState,
  BusinessTextField,
  SocialPlatform,
  SectionTextField,
  ItemTextField,
  ActionTextField,
  NavLinkField,
  ContactField,
} from './editorContext'

const LOGO_ASSET_ID = 'manual-logo'
const FAVICON_ASSET_ID = 'manual-favicon'

/** First section content of a given kind (used to seed businessInfo from content). */
function sectionContent(schema: SiteSchema, kind: string): SectionContent | undefined {
  for (const page of schema.pages) {
    for (const s of page.sections) {
      if (s.kind === kind) return s.content
    }
  }
  return undefined
}

function str(v: unknown): string | undefined {
  return typeof v === 'string' && v.trim() ? v : undefined
}

/** Map a list of social URLs onto known platforms by domain. */
function deriveSocial(urls: string[]): BusinessInfo['social'] {
  const s: Record<string, string> = {}
  for (const u of urls) {
    const low = u.toLowerCase()
    if (!s.facebook && low.includes('facebook.')) s.facebook = u
    else if (!s.instagram && low.includes('instagram.')) s.instagram = u
    else if (!s.x && (low.includes('twitter.') || low.includes('x.com'))) s.x = u
    else if (!s.linkedin && low.includes('linkedin.')) s.linkedin = u
    else if (!s.tiktok && low.includes('tiktok.')) s.tiktok = u
    else if (!s.youtube && (low.includes('youtube.') || low.includes('youtu.be'))) s.youtube = u
  }
  return s
}

/** Seed businessInfo from the generated schema's contact/footer content when absent. */
function withBusinessInfo(schema: SiteSchema): SiteSchema {
  if (schema.businessInfo) return schema
  const contact = sectionContent(schema, 'contact')
  const footer = sectionContent(schema, 'footer')
  const urls = [...(contact?.social ?? []), ...(footer?.social ?? [])]
  const businessInfo: BusinessInfo = {
    email: str(contact?.contact?.email),
    phone: str(contact?.contact?.phone),
    street: str(contact?.location?.address),
    social: deriveSocial(urls),
  }
  return { ...schema, businessInfo }
}

/** Return a new schema with a managed image asset set (src) or cleared (null). */
function applyImage(schema: SiteSchema, id: string, type: AssetType, src: string | null): SiteSchema {
  const items = schema.assets.items.filter((a) => a.id !== id)
  const key = type === 'favicon' ? 'favicon' : 'logo'
  if (src) items.push({ id, type, url: src })
  return {
    ...schema,
    branding: { ...schema.branding, [key]: src ? { assetId: id } : undefined },
    assets: { ...schema.assets, items },
  }
}

function assetUrl(schema: SiteSchema, assetId?: string): string {
  if (!assetId) return ''
  return schema.assets.items.find((a) => a.id === assetId)?.url ?? ''
}

/**
 * Return a new schema with a section's single background-media slot set (src) or cleared
 * (null), through the same asset-library indirection as `applyImage` — never inline base64
 * on the section. The asset id is scoped to the section so multiple media-bearing sections
 * (e.g. two heroes on future pages) never collide.
 */
function applySectionMedia(schema: SiteSchema, sectionId: string, src: string | null): SiteSchema {
  const assetId = `manual-media-${sectionId}`
  const items = schema.assets.items.filter((a) => a.id !== assetId)
  if (src) items.push({ id: assetId, type: 'cover', url: src })
  const pages = schema.pages.map((page) => ({
    ...page,
    sections: page.sections.map((s) =>
      s.id === sectionId
        ? { ...s, content: { ...s.content, media: src ? [{ id: assetId, type: 'image' as const, assetId }] : [] } }
        : s,
    ),
  }))
  return { ...schema, pages, assets: { ...schema.assets, items } }
}

/**
 * Return a new schema with a single repeated content item's `media` slot set (src) or
 * cleared (null) — e.g. a Services item's icon/image — through the same asset-library
 * indirection as `applySectionMedia`. The asset id is scoped to the item id so every item's
 * icon is independent even when several items exist on the same section.
 */
function applyItemMedia(schema: SiteSchema, sectionId: string, itemId: string, src: string | null): SiteSchema {
  const assetId = `manual-item-${itemId}`
  const items = schema.assets.items.filter((a) => a.id !== assetId)
  if (src) items.push({ id: assetId, type: 'image', url: src })
  const pages = schema.pages.map((page) => ({
    ...page,
    sections: page.sections.map((s) =>
      s.id === sectionId
        ? {
            ...s,
            content: {
              ...s.content,
              items: (s.content.items ?? []).map((it) =>
                it.id === itemId ? { ...it, media: src ? { id: assetId, type: 'image' as const, assetId } : undefined } : it,
              ),
            },
          }
        : s,
    ),
  }))
  return { ...schema, pages, assets: { ...schema.assets, items } }
}

/**
 * True when any asset id is still referenced from anywhere in the schema (branding, or any
 * section's `content.media[]` / per-item `content.items[].media`). Used before pruning an
 * asset on removal so a shared reference is never orphaned out from under another consumer.
 */
function isAssetReferenced(schema: SiteSchema, assetId: string): boolean {
  if (schema.branding.logo?.assetId === assetId || schema.branding.favicon?.assetId === assetId) return true
  return schema.pages.some((page) =>
    page.sections.some(
      (s) =>
        (s.content.media ?? []).some((m) => m.assetId === assetId) ||
        (s.content.items ?? []).some((it) => it.media?.assetId === assetId),
    ),
  )
}

/** Find a section's media item by its (MediaItem) id, searching only the first page. */
function findMediaItem(schema: SiteSchema, sectionId: string, mediaId: string): MediaItem | undefined {
  const section = schema.pages[0]?.sections.find((s) => s.id === sectionId)
  return section?.content.media?.find((m) => m.id === mediaId)
}

/**
 * Append a new gallery image: registers a fresh asset (never reusing/duplicating an existing
 * id) and appends a MediaItem pointing at it to the section's `content.media` list.
 */
function applyAddGalleryImage(schema: SiteSchema, sectionId: string, src: string): SiteSchema {
  const assetId = `manual-gallery-${newItemId()}`
  const mediaItem: MediaItem = { id: `media-${assetId}`, type: 'image', assetId }
  const pages = schema.pages.map((page) => ({
    ...page,
    sections: page.sections.map((s) =>
      s.id === sectionId ? { ...s, content: { ...s.content, media: [...(s.content.media ?? []), mediaItem] } } : s,
    ),
  }))
  return { ...schema, pages, assets: { ...schema.assets, items: [...schema.assets.items, { id: assetId, type: 'gallery', url: src }] } }
}

/**
 * Remove one gallery image by its MediaItem id. The backing asset is pruned too, UNLESS it is
 * still referenced elsewhere in the schema (see `isAssetReferenced`) — never orphan a shared
 * reference, but never leave an unused one behind either.
 */
function applyRemoveGalleryImage(schema: SiteSchema, sectionId: string, mediaId: string): SiteSchema {
  const item = findMediaItem(schema, sectionId, mediaId)
  const pages = schema.pages.map((page) => ({
    ...page,
    sections: page.sections.map((s) =>
      s.id === sectionId
        ? { ...s, content: { ...s.content, media: (s.content.media ?? []).filter((m) => m.id !== mediaId) } }
        : s,
    ),
  }))
  const next = { ...schema, pages }
  if (!item || isAssetReferenced(next, item.assetId)) return next
  return { ...next, assets: { ...next.assets, items: next.assets.items.filter((a) => a.id !== item.assetId) } }
}

/**
 * Replace a gallery image's bytes in place: the MediaItem (id + assetId) is untouched, only
 * the underlying Asset's `url` changes — so the image swaps live without creating a new asset
 * id or leaving the old one behind (no duplication, no orphan).
 */
function applyReplaceGalleryImage(schema: SiteSchema, sectionId: string, mediaId: string, src: string): SiteSchema {
  const item = findMediaItem(schema, sectionId, mediaId)
  if (!item) return schema
  return {
    ...schema,
    assets: { ...schema.assets, items: schema.assets.items.map((a) => (a.id === item.assetId ? { ...a, url: src } : a)) },
  }
}

/** Reorder a gallery image up/down within its section's `content.media` list. */
function applyMoveGalleryImage(schema: SiteSchema, sectionId: string, mediaId: string, dir: 'up' | 'down'): SiteSchema {
  const pages = schema.pages.map((page) => ({
    ...page,
    sections: page.sections.map((s) => {
      if (s.id !== sectionId) return s
      const list = [...(s.content.media ?? [])]
      const from = list.findIndex((m) => m.id === mediaId)
      const to = dir === 'up' ? from - 1 : from + 1
      if (from < 0 || to < 0 || to >= list.length) return s
      const [moving] = list.splice(from, 1)
      list.splice(to, 0, moving)
      return { ...s, content: { ...s.content, media: list } }
    }),
  }))
  return { ...schema, pages }
}

// ── Section (page[0]) immutable update helpers ─────────────────────────────────

/** Map the first page's sections through `fn`, returning a NEW schema. */
function mapSections(schema: SiteSchema, fn: (s: Section) => Section): SiteSchema {
  const pages = schema.pages.map((page, i) =>
    i === 0 ? { ...page, sections: page.sections.map(fn) } : page,
  )
  return { ...schema, pages }
}

/** Update the section with `id` via `fn` (all others untouched). */
function editSectionById(schema: SiteSchema, id: string, fn: (s: Section) => Section): SiteSchema {
  return mapSections(schema, (s) => (s.id === id ? fn(s) : s))
}

/** Update the FIRST section of a kind via `fn` (used to mirror Settings → content). */
function editFirstOfKind(schema: SiteSchema, kind: SectionKind, fn: (s: Section) => Section): SiteSchema {
  let done = false
  const pages = schema.pages.map((page, i) => {
    if (i !== 0) return page
    return {
      ...page,
      sections: page.sections.map((s) => {
        if (!done && s.kind === kind) {
          done = true
          return fn(s)
        }
        return s
      }),
    }
  })
  return { ...schema, pages }
}

/** Merge `content` fields onto a section (content is kind-agnostic, so no cast needed). */
function withContent(s: Section, content: SectionContent): Section {
  return { ...s, content }
}

/**
 * Build the initial working schema + seed theme overrides.
 *
 *  - Always seed businessInfo (withBusinessInfo) then mirror it onto the rendered
 *    content (applyBusinessInfoToContent) so Settings edits survive reload.
 *  - Freshly generated (source !== 'manual'): NORMALIZE only typography/radius/spacing to
 *    the reference default (unrelated to the color pipeline — untouched). Colors are NEVER
 *    normalized: `theme.primaryColor` and `branding.brandColor` are read straight from the
 *    AI-generated schema, exactly as `resolveTheme`/DesignPanel read them everywhere else,
 *    so the Design panel's initial values are never a fallback substituted for a value the
 *    schema actually has. Overrides are seeded to match (only emitting a var when the
 *    color differs from its reference default), so the initial preview is consistent with
 *    what the panel shows — no separate, disconnected "looks different until you touch it"
 *    state for colors specifically.
 *  - A prior save (source === 'manual'): KEEP the loaded theme and seed overrides by
 *    diffing it against the reference, so the live preview shows exactly the saved edits.
 */
function buildInitial(initialSchema: SiteSchema): { schema: SiteSchema; overrides: Record<string, string> } {
  const base = applyBusinessInfoToContent(withBusinessInfo(initialSchema))
  if (base.metadata.source === 'manual') {
    return { schema: base, overrides: themeOverrideVars(base.theme, base.branding.brandColor) }
  }
  const schema: SiteSchema = {
    ...base,
    theme: {
      ...base.theme,
      typography: { headingFont: REFERENCE_THEME.headingFont, bodyFont: REFERENCE_THEME.bodyFont },
      borderRadius: REFERENCE_THEME.borderRadius,
      spacing: REFERENCE_THEME.spacing,
    },
  }
  const overrides: Record<string, string> = {}
  if (schema.theme.primaryColor !== REFERENCE_THEME.primaryColor) overrides['--sb-primary'] = schema.theme.primaryColor
  if (schema.branding.brandColor !== REFERENCE_BRAND_COLOR) overrides['--sb-brand'] = schema.branding.brandColor
  return { schema, overrides }
}

let itemSeq = 0
/** Stable-enough id for a newly added content item (session scoped). */
function newItemId(): string {
  itemSeq += 1
  return `item-${Date.now().toString(36)}-${itemSeq}`
}

export function BuilderEditorProvider({
  builder,
  initialSchema,
  children,
}: {
  builder: BuilderRecord
  initialSchema: SiteSchema
  children: React.ReactNode
}) {
  const queryClient = useQueryClient()

  const [schema, setSchema] = useState<SiteSchema>(() => buildInitial(initialSchema).schema)
  const [saveState, setSaveState] = useState<SaveState>('saved')
  const [lastSavedAt, setLastSavedAt] = useState(builder.updatedAt)
  // Only the theme fields the user edits (CSS var → value): empty for a freshly generated
  // site (preview renders the pixel-perfect reference default); seeded from the saved
  // theme's diff-vs-reference for a prior save so the saved edits show live.
  const [themeOverrides, setThemeOverrides] = useState<Record<string, string>>(
    () => buildInitial(initialSchema).overrides,
  )

  const schemaRef = useRef(schema)
  const savingRef = useRef(false)
  // Only send logo/favicon when the user actually changed them (avoids rewriting the
  // AI-generated logo asset on every save).
  const logoTouched = useRef(false)
  const faviconTouched = useRef(false)

  // Live Preview ↔ sidebar sync (Section Navigation UX) — see editorContext.ts.
  const [previewActiveSectionId, setPreviewActiveSectionId] = useState<string | null>(null)
  const [scrollRequest, setScrollRequest] = useState<{ sectionId: string; nonce: number } | null>(null)
  const scrollNonce = useRef(0)
  const requestScrollToSection = useCallback((sectionId: string) => {
    scrollNonce.current += 1
    setScrollRequest({ sectionId, nonce: scrollNonce.current })
  }, [])

  const edit = useCallback((recipe: (prev: SiteSchema) => SiteSchema) => {
    setSchema((prev) => {
      const next = recipe(prev)
      schemaRef.current = next
      return next
    })
    setSaveState('dirty')
  }, [])

  /** Merge CSS-var overrides so the preview restyles just the edited fields. */
  const applyVars = useCallback((vars: Record<string, string>) => {
    setThemeOverrides((prev) => ({ ...prev, ...vars }))
  }, [])

  /** Edit a single section by id (immutable), streaming to the preview. */
  const editSection = useCallback(
    (id: string, fn: (s: Section) => Section) => edit((p) => editSectionById(p, id, fn)),
    [edit],
  )

  /** Update the items array of a section by id. */
  const editItems = useCallback(
    (id: string, fn: (items: readonly ContentItem[]) => readonly ContentItem[]) =>
      editSection(id, (s) => withContent(s, { ...s.content, items: fn(s.content.items ?? []) })),
    [editSection],
  )

  const save = useCallback(async () => {
    if (savingRef.current) return
    savingRef.current = true
    const s = schemaRef.current
    setSaveState('saving')
    try {
      const patch: BuilderUpdate = {
        theme: {
          designSystem: s.theme.designSystem,
          primaryColor: s.theme.primaryColor,
          typography: { headingFont: s.theme.typography.headingFont, bodyFont: s.theme.typography.bodyFont },
          borderRadius: s.theme.borderRadius,
          spacing: s.theme.spacing,
        },
        brandColor: s.branding.brandColor,
        seoTitle: s.seo.title,
        seoDescription: s.seo.description,
        logoSize: s.branding.logoSize,
        businessInfo: s.businessInfo,
        // The editor's full current working copy — replaces the stored sections/asset
        // library wholesale so per-section edits (nav links, headings, images, list
        // items, …) persist across reload instead of silently reverting.
        pages: s.pages,
        assets: s.assets.items,
      }
      if (logoTouched.current) patch.logo = assetUrl(s, s.branding.logo?.assetId)
      if (faviconTouched.current) patch.favicon = assetUrl(s, s.branding.favicon?.assetId)

      const updated = await siteBuilderApi.updateBuilder(builder.id, patch)
      queryClient.setQueryData(['builder', builder.id], updated)
      setLastSavedAt(updated.updatedAt)
      logoTouched.current = false
      faviconTouched.current = false
      setSaveState('saved')
    } catch {
      setSaveState('error')
    } finally {
      savingRef.current = false
    }
  }, [builder.id, queryClient])

  const setLogo = useCallback((src: string | null) => {
    logoTouched.current = true
    edit((p) => applyImage(p, LOGO_ASSET_ID, 'logo', src))
  }, [edit])
  const setFavicon = useCallback((src: string | null) => {
    faviconTouched.current = true
    edit((p) => applyImage(p, FAVICON_ASSET_ID, 'favicon', src))
  }, [edit])

  const value: BuilderEditorValue = {
    builder,
    schema,
    saveState,
    lastSavedAt,
    dirty: saveState === 'dirty' || saveState === 'error',
    themeOverrides,

    // ── Design: persist to schema.theme AND stream a matching --sb-* override ──────
    setDesignSystem: (v) => {
      const ds = DESIGN_SYSTEMS[v]
      edit((p) => ({
        ...p,
        theme: {
          ...p.theme,
          designSystem: v,
          typography: { headingFont: ds.typography.headingFont, bodyFont: ds.typography.bodyFont },
          borderRadius: ds.borderRadius,
          spacing: ds.spacing,
        },
      }))
      applyVars({
        '--sb-heading-font': fontStack(ds.typography.headingFont),
        '--sb-body-font': fontStack(ds.typography.bodyFont),
        '--sb-radius': RADIUS[ds.borderRadius],
        '--sb-radius-lg': RADIUS_LG[ds.borderRadius],
        '--sb-section-y': SPACING_Y[ds.spacing],
      })
    },
    // Primary color drives the site's visual chrome (navbar/footer/buttons/links/active
    // states, via --sb-primary — see resolveTheme.ts). Independent of brandColor: this
    // setter never touches branding.brandColor.
    setPrimaryColor: (v) => {
      edit((p) => ({ ...p, theme: { ...p.theme, primaryColor: v } }))
      applyVars({ '--sb-primary': v })
    },
    // Brand color is business-identity metadata (--sb-brand), independent of primaryColor:
    // this setter never touches theme.primaryColor. Each color field owns exactly one
    // value — see resolveTheme.ts for the full reasoning on why they're never merged.
    setBrandColor: (v) => {
      edit((p) => ({ ...p, branding: { ...p.branding, brandColor: v } }))
      applyVars({ '--sb-brand': v })
    },
    setHeadingFont: (v) => {
      edit((p) => ({ ...p, theme: { ...p.theme, typography: { ...p.theme.typography, headingFont: v } } }))
      applyVars({ '--sb-heading-font': fontStack(v) })
    },
    setBodyFont: (v) => {
      edit((p) => ({ ...p, theme: { ...p.theme, typography: { ...p.theme.typography, bodyFont: v } } }))
      applyVars({ '--sb-body-font': fontStack(v) })
    },
    setBorderRadius: (v) => {
      edit((p) => ({ ...p, theme: { ...p.theme, borderRadius: v } }))
      applyVars({ '--sb-radius': RADIUS[v], '--sb-radius-lg': RADIUS_LG[v] })
    },
    setSpacing: (v) => {
      edit((p) => ({ ...p, theme: { ...p.theme, spacing: v } }))
      applyVars({ '--sb-section-y': SPACING_Y[v] })
    },
    setLogo,
    setFavicon,
    setLogoSize: (px) => edit((p) => ({ ...p, branding: { ...p.branding, logoSize: px } })),
    setSeoTitle: (v) => edit((p) => ({ ...p, seo: { ...p.seo, title: v } })),
    setSeoDescription: (v) => edit((p) => ({ ...p, seo: { ...p.seo, description: v } })),

    // ── Settings: persist to businessInfo AND mirror to the rendered contact/footer ──
    setBusinessField: (field: BusinessTextField, val: string) =>
      edit((p) => {
        let next: SiteSchema = { ...p, businessInfo: { ...p.businessInfo, [field]: val } }
        if (field === 'email' || field === 'phone') {
          // Mirror into BOTH the contact section and the footer's Contact column.
          const setContact = (s: Section) =>
            withContent(s, { ...s.content, contact: { ...s.content.contact, [field]: val } })
          next = editFirstOfKind(next, 'contact', setContact)
          next = editFirstOfKind(next, 'footer', setContact)
        } else if (field === 'street') {
          const setLocation = (s: Section) =>
            withContent(s, { ...s.content, location: { ...s.content.location, address: val, mapUrl: mapEmbedUrl(val) } })
          next = editFirstOfKind(next, 'contact', setLocation)
          next = editFirstOfKind(next, 'footer', setLocation)
        }
        return next
      }),
    setSocial: (platform: SocialPlatform, val: string) =>
      edit((p) => {
        const social = { ...p.businessInfo?.social, [platform]: val }
        const urls = Object.values(social).filter((u): u is string => typeof u === 'string' && u.trim().length > 0)
        let next: SiteSchema = { ...p, businessInfo: { ...p.businessInfo, social } }
        next = editFirstOfKind(next, 'footer', (s) => withContent(s, { ...s.content, social: urls }))
        next = editFirstOfKind(next, 'contact', (s) => withContent(s, { ...s.content, social: urls }))
        return next
      }),

    // ── Sections (live working schema) ─────────────────────────────────────────────
    setSectionLayout: (id: string, layout: LayoutId) =>
      editSection(id, (s) => ({ ...s, layout } as Section)),
    toggleSectionVisible: (id: string) => editSection(id, (s) => ({ ...s, visible: !s.visible })),
    reorderSections: (movableIds: string[]) =>
      edit((p) => {
        const page = p.pages[0]
        if (!page) return p
        const list = page.sections
        // Anchors: navbar first, footer last — same guarantee the old up/down reorder had.
        const navbar = list[0]?.kind === 'navbar' ? list[0] : null
        const lastIndex = list.length - 1
        const footer = list[lastIndex]?.kind === 'footer' ? list[lastIndex] : null
        const currentMovable = list.filter((s) => s.kind !== 'navbar' && s.kind !== 'footer')

        const byId = new Map<string, Section>(currentMovable.map((s) => [s.id, s]))
        const reordered = movableIds.map((id) => byId.get(id)).filter((s): s is Section => !!s)
        // Anything but an exact permutation of the current movable set is a no-op (e.g. a
        // stale drag firing after the schema changed underneath it).
        if (reordered.length !== currentMovable.length) return p

        const sections = [...(navbar ? [navbar] : []), ...reordered, ...(footer ? [footer] : [])]
        const pages = p.pages.map((pg, i) => (i === 0 ? { ...pg, sections } : pg))
        return { ...p, pages }
      }),
    setSectionText: (id: string, field: SectionTextField, val: string) =>
      editSection(id, (s) => withContent(s, { ...s.content, [field]: val })),
    setItemField: (id: string, itemId: string, field: ItemTextField, val: string) =>
      editItems(id, (items) => items.map((it) => (it.id === itemId ? { ...it, [field]: val } : it))),
    setItemRating: (id: string, itemId: string, val: number) =>
      editItems(id, (items) => items.map((it) => (it.id === itemId ? { ...it, rating: val } : it))),
    addItem: (id: string) =>
      editItems(id, (items) => [...items, { id: newItemId(), title: 'New item' } as ContentItem]),
    removeItem: (id: string, itemId: string) =>
      editItems(id, (items) => items.filter((it) => it.id !== itemId)),
    moveItem: (id: string, itemId: string, dir: 'up' | 'down') =>
      editItems(id, (items) => {
        const list = [...items]
        const from = list.findIndex((it) => it.id === itemId)
        const to = dir === 'up' ? from - 1 : from + 1
        if (from < 0 || to < 0 || to >= list.length) return items
        const [moving] = list.splice(from, 1)
        list.splice(to, 0, moving)
        return list
      }),
    setItemImage: (id: string, itemId: string, src: string | null) =>
      edit((p) => applyItemMedia(p, id, itemId, src)),
    setActionField: (id: string, actionId: string, field: ActionTextField, val: string) =>
      editSection(id, (s) =>
        withContent(s, {
          ...s.content,
          actions: (s.content.actions ?? []).map((a: Action) => (a.id === actionId ? { ...a, [field]: val } : a)),
        }),
      ),
    setNavLink: (id: string, itemId: string, field: NavLinkField, val: string) =>
      editItems(id, (items) => items.map((it) => (it.id === itemId ? { ...it, [field]: val } : it))),
    setContactField: (id: string, field: ContactField, val: string) =>
      edit((p) => {
        const setContact = (s: Section) =>
          withContent(s, { ...s.content, contact: { ...s.content.contact, [field]: val } })
        // Edit the target (contact) section AND mirror into the footer's Contact column.
        const next = editSectionById(p, id, setContact)
        return editFirstOfKind(next, 'footer', setContact)
      }),
    setLocationField: (id: string, _field: 'address', val: string) =>
      edit((p) => {
        const setLocation = (s: Section) =>
          withContent(s, { ...s.content, location: { ...s.content.location, address: val, mapUrl: mapEmbedUrl(val) } })
        const next = editSectionById(p, id, setLocation)
        return editFirstOfKind(next, 'footer', setLocation)
      }),
    setSectionImage: (id: string, src: string | null) => edit((p) => applySectionMedia(p, id, src)),
    addGalleryImage: (id: string, src: string) => edit((p) => applyAddGalleryImage(p, id, src)),
    removeGalleryImage: (id: string, mediaId: string) => edit((p) => applyRemoveGalleryImage(p, id, mediaId)),
    replaceGalleryImage: (id: string, mediaId: string, src: string) => edit((p) => applyReplaceGalleryImage(p, id, mediaId, src)),
    moveGalleryImage: (id: string, mediaId: string, dir: 'up' | 'down') => edit((p) => applyMoveGalleryImage(p, id, mediaId, dir)),

    previewActiveSectionId,
    setPreviewActiveSectionId,
    scrollRequest,
    requestScrollToSection,

    save: () => void save(),
  }

  return <BuilderEditorCtx.Provider value={value}>{children}</BuilderEditorCtx.Provider>
}
